import { StateCreator } from "zustand";
import { produce } from "immer";
import { appStore } from "@/app/store/appStore";
import {
	FtlSharedGeneratedApiFtlApplicationHandlersGetLauncherAppManifestGetLauncherAppManifestResult,
	ResponseError
} from "@/api/gen";
import OpenApiClient from "@/api/OpenApiClient";
import { IErrorResponse } from "@/features/StepsContent/components/StepFolder/utils/types";
import { ManifestJsonData } from "@features/FxLauncherLoader/utils/interfaces";
import { wrapStore } from "@/app/store/StoreWrapper";
import { persistStore } from "@entities/StepFolder/store/persistStore";
import FilesystemRoot = FxApi.File.FilesystemRoot;
import TorrentInfo = FxApi.Torrent.TorrentInfo;
import TorrentState = FxApi.Torrent.TorrentState;

export const FREE_SPACE_WARNING_LIMIT = 1024 * 1024 * 1024; // 1GB
export const FREE_SPACE_ERROR_LIMIT = 100 * 1024 * 1024; // 100MB

export interface IFolderStepStore {
	branches: string[];

	validateCode: (code: string) => Promise<void>;
	validateCodeErrors: string[];
	clearValidateCodeErrors: () => void;

	error: string | null;
	clearError: () => void;
	appProfile: FtlSharedGeneratedApiFtlApplicationHandlersGetLauncherAppManifestGetLauncherAppManifestResult | null;
	appProfileLoaded: boolean;
	setAppProfileLoaded: (loaded: boolean) => void;
	setAppProfile: (
		profile: FtlSharedGeneratedApiFtlApplicationHandlersGetLauncherAppManifestGetLauncherAppManifestResult | null
	) => void;
	setError: (error: string | null) => void;
	appSizeBytes: number;
	freeSpaceBytes: number;
	installedSizeBytes: number;
	updateSizeBytes: number;
	setAppSizeBytes: (bytes: number) => void;
	setFreeSpaceBytes: (bytes: number) => void;
	requiredSpaceBytes: number;
	setRequiredSpaceBytes: (bytes: number) => void;
	setInstalledSizeBytes: (bytes: number) => void;
	setUpdateSizeBytes: (bytes: number) => void;

	manifest: ManifestJsonData | null;
	setManifest: (manifest: ManifestJsonData | null) => void;
	roots: Record<FilesystemRoot, string>;
	setRoots: (roots: Record<FilesystemRoot, string>) => void;
	setBranches: (branches: string[]) => void;
	torrentInfo: TorrentInfo | null;
	setTorrentInfo: (info: TorrentInfo | null) => void;
	torrentState: TorrentState|null;
	setTorrentState: (state: TorrentState | null) => void;
}

export const createFolderStepStore: StateCreator<IFolderStepStore> = (set) => ({
	branches: [],
	requiredSpaceBytes: 0,
	setRequiredSpaceBytes: (bytes: number) => {
		set(
            produce((store: IFolderStepStore) => {
                store.requiredSpaceBytes = bytes;
            })
        );
	},
	torrentState: null,
	setTorrentState: (state: TorrentState | null) => {
		set(
            produce((store: IFolderStepStore) => {
                store.torrentState = state;
            })
        );
	},
	torrentInfo: null,
	setTorrentInfo: (info: TorrentInfo | null) => {
		set(
            produce((store: IFolderStepStore) => {
                store.torrentInfo = info;
            })
        );
	},
	appProfile: null,
	appProfileLoaded: false,
	setAppProfileLoaded: (loaded: boolean) => {
		set(
			produce((store: IFolderStepStore) => {
				store.appProfileLoaded = loaded;
			})
		);
	},
	setBranches: (branches: string[]) => {
		set(
			produce((store: IFolderStepStore) => {
				store.branches = branches;
			})
		);
	},
	appSizeBytes: 0,
	roots: { APP: "", APP_DATA: "", CACHE: "", WIN: "", TMP: "", HOME: "" },
	setRoots: (roots: Record<FilesystemRoot, string>) => {
		set(
			produce((store: IFolderStepStore) => {
				store.roots = roots;
			})
		);
	},
	freeSpaceBytes: 0,
	installedSizeBytes: 0,
	updateSizeBytes: 0,
	manifest: null,
	setManifest: (manifest: ManifestJsonData | null) => {
		set(
			produce((store: IFolderStepStore) => {
				store.manifest = manifest;
			})
		);
	},

	setAppSizeBytes: (bytes: number) => {
		set(
			produce((store: IFolderStepStore) => {
				store.appSizeBytes = bytes;
			})
		);
	},
	setFreeSpaceBytes: (bytes: number) => {
		set(
			produce((store: IFolderStepStore) => {
				store.freeSpaceBytes = bytes;
			})
		);
	},
	setInstalledSizeBytes: (bytes: number) => {
		set(
			produce((store: IFolderStepStore) => {
				store.installedSizeBytes = bytes;
			})
		);
	},
	setUpdateSizeBytes: (bytes: number) => {
		set(
			produce((store: IFolderStepStore) => {
				store.updateSizeBytes = bytes;
			})
		);
	},

	setAppProfile: (
		profile: FtlSharedGeneratedApiFtlApplicationHandlersGetLauncherAppManifestGetLauncherAppManifestResult | null
	) => {
		set(
			produce((store: IFolderStepStore) => {
				store.appProfile = profile;
			})
		);
	},
	freeSpaceHint: "",
	validateCodeErrors: [],
	error: null,
	setError: (error: string | null) => {
		set(
			produce((store: IFolderStepStore) => {
				store.error = error;
			})
		);
	},
	clearError: () => {
		set(
			produce((store: IFolderStepStore) => {
				store.error = null;
			})
		);
	},


	validateCode: async (code: string) => {
		console.log("Activating ftl code %o", code);
		if (!code.length) {
			set(
				produce((store: IFolderStepStore) => {
					store.validateCodeErrors = ["code field empty"];
				})
			);
			return;
		}

		const { appCode } = appStore.getState();

		try {
			const res = await OpenApiClient.FtlIntegration.fxIdWebFeaturesFtlIntegrationLauncherActivateKeyEndpoint(
				appCode,
				{
					Key: code
				}
			);

			set(
				produce((store: IFolderStepStore) => {
					store.validateCodeErrors = [];
					const branches = [...folderStepStore.getState().branches, res.ActivatedBranch];
					store.branches = branches;
				})
			);
			persistStore.getState().setBranch(res.ActivatedBranch);
		} catch (error) {
			try {
				if (error instanceof ResponseError) {
					const text = await error.response.text();
					const responseObject = JSON.parse(text);
					console.log(responseObject);
					set(
						produce((store: IFolderStepStore) => {
							store.validateCodeErrors = (responseObject as IErrorResponse).Errors.generalErrors;
						})
					);
				}
			} catch (errorRead) {
				console.error("Ошибка при чтении тела ответа:", errorRead);
			}
			throw new Error("failed to check key");
		}
	},
	clearValidateCodeErrors: () => {
		set(
			produce((store: IFolderStepStore) => {
				store.validateCodeErrors = [];
			})
		);
	}
});

export const folderStepStoreWrapper = wrapStore<IFolderStepStore>();
export const folderStepStore = folderStepStoreWrapper.withStore(createFolderStepStore, "folder-step-store");
